import { morphSection } from '@theme/section-renderer';

/**
 * TK service-line controller.
 *
 * Keeps the cart's hidden service lines in step with the fulfilment choice:
 *  - the tk-delivery-fee line (Delivery), priced by the postal tier;
 *  - the $0 tk-pickup-marker line (Pickup);
 *  - the 📅 day note, which lives only on whichever of those two is present;
 *  - the Est. weight note on weighed lines that arrived without one;
 *  - the Total weight on freshly sliced deli lines ("1.5 lb"),
 *    kept in step with the quantity so the order says what was ordered.
 * The cart UI hides the service lines; this controller re-adds them if
 * anything removes them — self-healing lines, never a customer decision.
 *
 * Inputs:
 *  - static config + per-line estimates: the [data-tk-cart-config] JSON that
 *    snippets/tk-cart-config.liquid renders inside the cart summary and
 *    re-renders on every section morph (the estimates need custom.price_per_kg,
 *    which only Liquid can see);
 *  - authoritative cart state: /cart.js, fetched right before any mutation so
 *    a mid-morph stale DOM can't cause a wrong add or remove.
 *
 * Triggers: page load, every StandardEvents.cartLinesUpdate and every acked
 * picker save. After its own mutation it re-checks, so overlapping updates
 * converge.
 *
 * (Until 2026-10 this file also sized a "Weight allowance" card hold. The
 * store stopped taking payment online, so the allowance is gone; carts that
 * still hold one from before are cleared by the first pass below.)
 */

const CONFIG_SELECTOR = '[data-tk-cart-config]';
const MAX_PASSES = 4;

function theme() {
  // eslint-disable-next-line no-undef
  return typeof Theme !== 'undefined' ? Theme : {};
}

function readConfig() {
  const nodes = document.querySelectorAll(CONFIG_SELECTOR);
  const node = nodes[nodes.length - 1];
  if (!node) return null;
  try {
    return JSON.parse(node.textContent || '');
  } catch {
    return null;
  }
}

/** @returns {Promise<any>} the live cart */
async function fetchCart() {
  const response = await fetch(`${theme().routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`cart fetch failed: ${response.status}`);
  return response.json();
}

function cartSectionIds() {
  const ids = new Set();
  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.add(component.dataset.sectionId);
    }
  }
  return Array.from(ids);
}

/** hydration keeps an open drawer open; full is right for the cart page */
function morphModeFor(sectionId) {
  const component = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
  return component instanceof HTMLElement && component.dataset.drawer !== undefined ? 'hydration' : 'full';
}

async function morphFromResponse(sections) {
  if (!sections) return;
  for (const [sectionId, html] of Object.entries(sections)) {
    if (typeof html === 'string') await morphSection(sectionId, html, { mode: morphModeFor(sectionId) });
  }
}

/**
 * Freshly sliced deli comes in fixed amounts and the quantity counts them.
 * `spec` is the size snippets/tk-slice-total.liquid reads from the variant
 * name: {qlb} quarter pounds, or {g} grams on the older 100g sizes. Same
 * wording as that snippet, which the cart rows use — keep the two in step
 * (assets/tk-slice-total.js carries a copy for the product page).
 * @param {{qlb?: number, g?: number}} spec
 * @param {number} count
 */
function sliceAmount(spec, count) {
  if (spec.qlb) {
    const quarters = spec.qlb * count;
    return `${quarters / 4} lb`;
  }
  const grams = (spec.g || 0) * count;
  if (grams >= 1000) return grams % 1000 === 0 ? `${grams / 1000} kg` : `${Math.round(grams / 10) / 100} kg`;
  return `${grams}g`;
}

/** The line's total weight alone: "1.5 lb", never "3 × 1/2 lb". */
function sliceTotalText(spec, quantity) {
  return sliceAmount(spec, Math.max(1, quantity || 1));
}

/**
 * The header bubble must never count the delivery-fee or pickup-marker lines.
 * Stock cart events can set it from raw totalQuantity (and persist that to
 * sessionStorage), so after every reconcile the bubble is corrected from
 * the live cart.
 * @param {any} cart /cart.js payload
 * @param {number|Array<number>} excludedProductIds
 */
function correctCartBubble(cart, excludedProductIds) {
  const excluded = new Set(Array.isArray(excludedProductIds) ? excludedProductIds : [excludedProductIds]);
  let count = 0;
  for (const item of cart.items) {
    if (excluded.has(item.product_id)) continue;
    // One item whatever the quantity: a freshly sliced line (its weight —
    // cart-icon.js) and a gift card (the $1 "Custom amount" variant carries
    // the dollars as the quantity).
    const single = /freshly sliced/i.test(item.variant_title || '') || item.handle === 'gift-card';
    count += single ? 1 : item.quantity;
  }
  for (const icon of document.querySelectorAll('cart-icon')) {
    /** @type {any} */ (icon).renderCartBubble?.(count, false);
  }
}

class WeightHoldController {
  #busy = false;
  #dirty = false;
  #passes = 0;
  #rechecks = 0;

  start() {
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
          this.#passes = 0;
          this.#rechecks = 0;
          // Correct the header bubble IMMEDIATELY — the reconcile below can
          // take several network round-trips, and until it finishes the
          // stock event handlers leave a stale or raw count on screen.
          const quickBubble = async () => {
            try {
              const config = readConfig();
              correctCartBubble(await fetchCart(), [config.fee?.productId || 0, config.marker?.productId || 0]);
            } catch {
              // next reconcile pass corrects it
            }
          };
          const promise = /** @type {any} */ (event).promise;
          if (promise?.then) {
            promise.then(quickBubble, quickBubble);
            promise.then(() => this.check()).catch(() => this.check());
          } else {
            quickBubble();
            this.check();
          }
        });
      })
      .catch(() => {
        // No standard-events bundle: fall back to a light poll after changes.
        document.addEventListener('change', () => this.check(), { passive: true });
      });

    // The fulfilment picker announces every acked attribute save; the fee
    // and marker lines (and the day note they carry) must follow it —
    // attribute-only writes never fire cartLinesUpdate.
    document.addEventListener('tk:fulfilment-synced', () => {
      this.#passes = 0;
      this.#rechecks = 0;
      this.check();
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.check(), { once: true });
    } else {
      this.check();
    }

    // Keep the Est. weight line property in step with the chosen variant:
    // the variant id input is updated by JS on variant change, so the
    // property input is re-synced just before the form serializes.
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        const property = form.querySelector('[data-tk-est-weight]');
        if (!(property instanceof HTMLInputElement)) return;
        const variantId = form.querySelector('input[name="id"]')?.value;
        if (!variantId) return;
        try {
          const weights = JSON.parse(property.dataset.tkWeights || '{}');
          // An empty value clears the property for rate-less variants, so a
          // stale weight never sticks after a variant switch.
          if (variantId in weights) property.value = weights[variantId];
        } catch {
          /* leave the server-rendered value */
        }
      },
      { capture: true }
    );
  }

  async check() {
    if (window.Shopify?.designMode) return;
    if (this.#busy) {
      this.#dirty = true;
      return;
    }
    this.#busy = true;
    try {
      await this.#reconcile();
    } catch (error) {
      console.warn('[tk-service-lines]', error);
    } finally {
      this.#busy = false;
      if (this.#dirty) {
        this.#dirty = false;
        this.check();
      }
    }
  }

  async #reconcile() {
    // Let any in-flight section morph land so the config JSON is fresh.
    await new Promise((resolve) => setTimeout(resolve, 60));

    const config = readConfig();
    if (!config || !config.enabled) return;
    if (this.#passes >= MAX_PASSES) return;

    const cart = await fetchCart();

    // Leftover "Weight allowance" lines from before the store stopped taking
    // payment online. Nothing hides them any more, so they would show as a
    // real item and ride into checkout. Remove on sight; this block can go
    // once the tk-weight-allowance product is deleted (Shopify then drops
    // the line itself).
    const staleAllowance = cart.items.filter((item) => item.handle === 'tk-weight-allowance');
    if (staleAllowance.length > 0) {
      this.#passes += 1;
      const updates = {};
      for (const line of staleAllowance) updates[String(line.variant_id)] = 0;
      const response = await fetch(`${theme().routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: cartSectionIds().join(','), sections_url: window.location.pathname }),
      });
      if (response.ok) await morphFromResponse((await response.json()).sections);
      else console.error('[tk-service-lines] could not clear a leftover allowance line:', response.status);
      this.#scheduleRecheck();
      return;
    }

    // The chosen day rides the service tile as a visible property: the
    // checkout order summary renders line properties but never cart
    // attributes. (Pickup is booked by day only — no time windows.)
    const strings = config.strings || {};
    const pickupLabel = strings.slotPickupLabel || '📅 Your pickup';
    const deliveryLabel = strings.slotDeliveryLabel || '📅 Your delivery day';
    const attrs = cart.attributes || {};

    // Gift cards only (no line needs shipping): there is nothing to deliver
    // or pick up, so the cart page shows no picker and the gate opens
    // without one (snippets/tk-cart-gift-only.liquid is the Liquid twin of
    // this test). A choice left over from earlier must not keep a fee or
    // marker line on the order — and a stale `Fulfilment: Delivery` with no
    // fee line would trip the SMART app's "fee missing" block at checkout —
    // so the attributes are cleared and the method is treated as none.
    const serviceIds = new Set([config.fee?.productId || 0, config.marker?.productId || 0]);
    const realLines = cart.items.filter((item) => !serviceIds.has(item.product_id));
    const giftOnly = realLines.length > 0 && !realLines.some((item) => item.requires_shipping);
    if (giftOnly && (attrs['Fulfilment'] || attrs['_tk_delivery_fee'] || attrs['Fulfilment day'])) {
      this.#passes += 1;
      const response = await fetch(`${theme().routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: { Fulfilment: '', 'Fulfilment day': '', _tk_day_iso: '', _tk_delivery_fee: '' },
          sections: cartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (response.ok) await morphFromResponse((await response.json()).sections);
      else console.error('[tk-service-lines] could not clear the fulfilment choice on a gift-card cart:', response.status);
      this.#scheduleRecheck();
      return;
    }
    const method = giftOnly ? '' : attrs['Fulfilment'];

    const slotDay = String(attrs['Fulfilment day'] || '').trim();
    const slotProps = {};
    if (method === 'Pickup' && slotDay) {
      slotProps[pickupLabel] = slotDay;
    } else if (method === 'Delivery' && slotDay) {
      slotProps[deliveryLabel] = slotDay;
    }

    // Delivery fee line: the store's only rate is the $0 "Local delivery"
    // shipping rate, so the picker's tier fee rides as a line of the hidden
    // tk-delivery-fee product (variant matched by price). It also hosts the
    // day note at checkout — see the slot sweep below.
    const fee = config.fee || {};
    const feeProductId = fee.productId || 0;
    const marker = config.marker || {};
    const markerProductId = marker.productId || 0;
    const feeVariants = fee.variants || {};
    const feeLines = feeProductId ? cart.items.filter((item) => item.product_id === feeProductId) : [];
    // fee.enabled ON is the normal setup (owner decision, 2026-08-24):
    // delivery rides a $0 SHIPPING rate so orders stay editable for the
    // weigh-down (native Local delivery orders can't be edited — verified),
    // and this line does the charging. With it OFF the block still runs
    // with a zero target, so stray fee lines get cleared.
    const feeCents =
      fee.enabled && method === 'Delivery' ? parseInt(attrs['_tk_delivery_fee'] || '0', 10) || 0 : 0;
    const feeVariantId = feeCents > 0 ? feeVariants[String(feeCents)] : null;
    if (feeCents > 0 && !feeVariantId) {
      console.error('[tk-service-lines] no delivery-fee variant for', feeCents, '— check the tk-delivery-fee product.');
    } else if (feeProductId) {
      const feeCorrect =
        (!feeVariantId && feeLines.length === 0) ||
        (feeLines.length === 1 &&
          feeLines[0].quantity === 1 &&
          feeLines[0].variant_id === feeVariantId &&
          feeLines[0].original_line_price === feeCents);
      if (!feeCorrect) {
        this.#passes += 1;
        const sections = cartSectionIds();
        let response = null;
        if (feeLines.length > 0) {
          const updates = {};
          for (const line of feeLines) updates[String(line.variant_id)] = 0;
          const body = { updates };
          if (!feeVariantId) {
            body.sections = sections.join(',');
            body.sections_url = window.location.pathname;
          }
          response = await fetch(`${theme().routes?.cart_update_url || '/cart/update'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
        }
        if (feeVariantId) {
          response = await fetch(theme().routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: feeVariantId, quantity: 1, properties: slotProps }],
              sections: sections.join(','),
              sections_url: window.location.pathname,
            }),
          });
        }
        if (response && response.ok) {
          const payload = await response.json();
          await morphFromResponse(payload.sections);
        } else if (response) {
          console.error('[tk-service-lines] delivery-fee line update failed:', response.status);
        }
        correctCartBubble(await fetchCart(), [feeProductId, markerProductId]);
        this.#scheduleRecheck();
        return;
      }
    }

    // Pickup marker line: a $0 item living in a RATELESS shipping profile.
    // Its presence makes the whole cart unshippable, which is what disables
    // checkout's Ship side for pickup orders — the mirror of the fee line
    // (whose pickup-location-free inventory disables the Pickup side for
    // delivery orders). Managed here with the same clear/add pattern.
    const markerLines = markerProductId ? cart.items.filter((item) => item.product_id === markerProductId) : [];
    const markerVariantId = method === 'Pickup' && marker.variantId ? marker.variantId : null;
    if (markerProductId) {
      const markerCorrect =
        (!markerVariantId && markerLines.length === 0) ||
        (markerVariantId && markerLines.length === 1 && markerLines[0].quantity === 1);
      if (!markerCorrect) {
        this.#passes += 1;
        const sections = cartSectionIds();
        let response = null;
        if (markerLines.length > 0 && (!markerVariantId || markerLines.length > 1)) {
          const updates = {};
          for (const line of markerLines) updates[String(line.variant_id)] = 0;
          const body = { updates };
          if (!markerVariantId) {
            body.sections = sections.join(',');
            body.sections_url = window.location.pathname;
          }
          response = await fetch(`${theme().routes?.cart_update_url || '/cart/update'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
        }
        if (markerVariantId && markerLines.length === 0) {
          response = await fetch(theme().routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: markerVariantId, quantity: 1, properties: slotProps }],
              sections: sections.join(','),
              sections_url: window.location.pathname,
            }),
          });
        }
        if (response && response.ok) {
          const payload = await response.json();
          await morphFromResponse(payload.sections);
        } else if (response) {
          console.error('[tk-service-lines] pickup-marker line update failed:', response.status);
        }
        correctCartBubble(await fetchCart(), [feeProductId, markerProductId]);
        this.#scheduleRecheck();
        return;
      }
    }

    // The Est. weight estimates come from the config (metafield knowledge),
    // keyed by line; config lines for since-removed items are simply skipped.
    const configKeys = new Set((config.lines || []).map((line) => line.key));

    // Live lines the config hasn't classified yet (an add landed after the
    // last morph): don't guess — wait for the next render pass.
    const unknownLines = cart.items.filter(
      (item) => !configKeys.has(item.key) && item.product_id !== feeProductId && item.product_id !== markerProductId
    );
    if (unknownLines.length > 0) {
      this.#scheduleRecheck();
      return;
    }

    // Slot sweep: the day note lives on the SERVICE tile — the Delivery fee
    // line for delivery orders, the Pickup marker line for pickup orders
    // (owner design, 2026-08-24). Those lines are controller-managed, so
    // the note can never wander onto real products; any product line still
    // carrying one (from the old first-line design) is stripped here.
    // Every stray line is fixed in this one pass (a property edit re-keys
    // only the edited line, so the other keys stay valid), then one morph.
    // Other properties (Est. weight) are preserved.
    const slotKeys = [pickupLabel, deliveryLabel];
    let slotHost;
    if (method === 'Delivery' && feeProductId) {
      slotHost = cart.items.find((item) => item.product_id === feeProductId);
    } else if (method === 'Pickup' && markerProductId) {
      slotHost = cart.items.find((item) => item.product_id === markerProductId);
    }
    // Weighed lines that arrived without their Est. weight note (a cart
    // permalink, a shared cart) get it written here from the config's
    // per-line estimate — same pass, same /cart/change, one morph.
    const estLabel = strings.estLabel || '';
    const estByKey = new Map((config.lines || []).map((line) => [line.key, line.est || '']));
    const missingEst = (line) =>
      Boolean(estLabel) && Boolean(estByKey.get(line.key)) && !((line.properties || {})[estLabel] || '').trim();

    // Freshly sliced deli rides in the cart as its 1/4 lb size: the product
    // form adds 2 × 1 lb as 8 × 1/4 lb (product-form.js), so every add of a
    // coldcut stacks onto one line, and the cart shows that line as its
    // weight. Its "Total weight" property is written only on the way to
    // checkout (stampSlicedLines below): a property that changed with the
    // quantity would make Shopify start a new line on the next add.
    const sliceLabel = strings.sliceLabel || '';
    const sliceByKey = new Map((config.lines || []).map((line) => [line.key, line.slice || null]));
    const sliceUnit = (line) => (sliceLabel ? sliceByKey.get(line.key) || null : null);

    // Safety net for lines that didn't come through the product form (an
    // older cart, a quick add, a shared link): two lines of one coldcut fold
    // into one — into the 1/4 lb size when their sizes differ. Every size is
    // priced at an exact multiple of the 1/4 lb price, so the total never
    // moves. Lines with their own note or choices stay apart.
    const sliceBaseByKey = new Map((config.lines || []).map((line) => [line.key, line.sliceBase || null]));
    const sliceIdentity = (line) => {
      const props = { ...(line.properties || {}) };
      for (const key of [sliceLabel, estLabel, ...slotKeys]) delete props[key];
      const kept = Object.entries(props)
        .filter(([, value]) => String(value ?? '').trim() !== '')
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
      const unit = sliceUnit(line);
      const scope = unit?.qlb && sliceBaseByKey.get(line.key) ? `p${line.product_id}` : `v${line.variant_id}`;
      return `${scope}|${JSON.stringify(kept)}`;
    };
    const sliceGroups = new Map();
    for (const line of cart.items) {
      if (!sliceUnit(line)) continue;
      const id = sliceIdentity(line);
      sliceGroups.set(id, [...(sliceGroups.get(id) || []), line]);
    }
    const twins = [...sliceGroups.values()].find((group) => group.length > 1);
    if (twins) {
      this.#passes += 1;
      let variantId = twins[0].variant_id;
      let quantity = twins.reduce((sum, line) => sum + line.quantity, 0);
      if (twins.some((line) => line.variant_id !== variantId)) {
        const base = sliceBaseByKey.get(twins[0].key);
        const quarters = twins.reduce((sum, line) => sum + (sliceUnit(line)?.qlb || 0) * line.quantity, 0);
        variantId = base.id;
        quantity = Math.round(quarters / base.qlb);
      }
      // Keep the plainest line of that size (no checkout stamp), so the next
      // product-form add stacks onto it.
      const candidates = twins.filter((line) => line.variant_id === variantId);
      const keep = candidates.find((line) => !(line.properties || {})[sliceLabel]) || candidates[0];
      const updates = {};
      for (const line of twins) updates[line.key] = line === keep ? quantity : 0;
      const sections = { sections: cartSectionIds().join(','), sections_url: window.location.pathname };
      // One request when the size to keep is already in the cart.
      let response = await fetch(`${theme().routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, ...(keep ? sections : {}) }),
      });
      // Only 1/2 lb and 1 lb in the cart: add the 1/4 lb size in their place.
      if (response.ok && !keep) {
        const props = { ...(twins[0].properties || {}) };
        delete props[sliceLabel];
        response = await fetch(theme().routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity, properties: props }], ...sections }),
        });
      }
      if (response.ok) await morphFromResponse((await response.json()).sections);
      else console.warn('[tk-service-lines] sliced line fold failed:', response.status);
      correctCartBubble(await fetchCart(), [feeProductId, markerProductId]);
      this.#scheduleRecheck();
      return;
    }

    const strays = cart.items.filter((line) => {
      const expected = line === slotHost ? slotProps : {};
      if (missingEst(line)) return true;
      return slotKeys.some((key) => (expected[key] || '') !== ((line.properties || {})[key] || ''));
    });
    if (strays.length > 0) {
      this.#passes += 1;
      const sweepSections = cartSectionIds();
      let lastPayload = null;
      for (const line of strays) {
        const expected = line === slotHost ? slotProps : {};
        const merged = { ...(line.properties || {}) };
        // '' instead of delete: /cart/change treats an EMPTY properties
        // hash as "nothing sent" and keeps the old properties, so a line
        // whose only property is the slot key could never be cleaned by
        // deletion — every strip silently no-opped (seen live; this was
        // why stale day stamps kept surviving). Blank-valued properties
        // stick, and every renderer skips blanks.
        for (const key of slotKeys) merged[key] = expected[key] || '';
        if (missingEst(line)) merged[estLabel] = estByKey.get(line.key);
        const changeResponse = await fetch(`${theme().routes?.cart_change_url || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: line.key,
            quantity: line.quantity,
            properties: merged,
            sections: sweepSections.join(','),
            sections_url: window.location.pathname,
          }),
        });
        if (changeResponse.ok) {
          lastPayload = await changeResponse.json();
        } else {
          // A strip can merge twin lines and retire a later key; the
          // scheduled re-check finishes the job on the fresh cart.
          console.warn('[tk-service-lines] slot property update failed:', changeResponse.status);
        }
      }
      if (lastPayload) await morphFromResponse(lastPayload.sections);
      correctCartBubble(await fetchCart(), [feeProductId, markerProductId]);
      this.#scheduleRecheck();
      return;
    }

    correctCartBubble(cart, [feeProductId, markerProductId]);
  }

  /**
   * On the way to checkout: write each freshly sliced line's weight onto it
   * as a "Total weight" property ("2 lb"), so checkout, the order in admin
   * and any slip print it. Bounded by the caller; a failure only leaves the
   * label off (the emails work the weight out themselves).
   */
  async stampSlicedLines() {
    const config = readConfig();
    if (!config) return;
    const label = config.strings?.sliceLabel || '';
    if (!label) return;
    const specs = new Map((config.lines || []).map((line) => [line.key, line.slice || null]));
    const cart = await fetchCart();
    for (const line of cart.items) {
      const spec = specs.get(line.key);
      if (!spec) continue;
      const text = sliceTotalText(spec, line.quantity);
      if ((line.properties || {})[label] === text) continue;
      const response = await fetch(`${theme().routes?.cart_change_url || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: { ...(line.properties || {}), [label]: text } }),
      });
      if (!response.ok) console.warn('[tk-service-lines] could not label a sliced line:', response.status);
    }
  }

  #scheduleRecheck() {
    if (this.#rechecks >= 5) return;
    this.#rechecks += 1;
    setTimeout(() => this.check(), 250);
  }
}

// A freshly sliced line's quantity box shows its weight (snippets/quantity-
// selector.liquid tk_display). Repaint it as soon as a stepper changes the
// count, before the section re-renders.
document.addEventListener('quantity-selector:update', (event) => {
  const wrapper = event.target instanceof Element ? event.target.closest('[data-tk-slice-spec]') : null;
  const text = wrapper?.querySelector('[data-tk-qty-weight]');
  if (!(wrapper instanceof HTMLElement) || !text) return;
  try {
    const spec = JSON.parse(wrapper.dataset.tkSliceSpec || '');
    const quantity = Number(/** @type {any} */ (event).detail?.quantity ?? /** @type {any} */ (event).quantity);
    if (quantity > 0) text.textContent = sliceAmount(spec, quantity);
  } catch {
    /* the re-render paints it */
  }
});

const tkServiceLines = new WeightHoldController();
tkServiceLines.start();
// tk-fulfilment.js calls this just before it leaves for checkout.
window.tkStampSlicedLines = () => tkServiceLines.stampSlicedLines();
